/**
 * Routing: one route per screen in docs/08-screens.md.
 *
 * The `roles` on each guarded route mirror the permission matrix in docs/02.
 * They decide what is shown, not what is allowed: the server authorises every
 * request independently (S-06).
 */

import { Navigate, createBrowserRouter } from "react-router-dom";
import { AppShell } from "@/components/layout/AppShell";
import { RequireAuth } from "@/components/layout/RequireAuth";
import { LoginForm } from "@/features/auth/LoginForm";
import { SignupPage } from "@/features/auth/SignupPage";
import { VolunteerHome } from "@/features/entry/VolunteerHome";
import { EntryForm } from "@/features/entry/EntryForm";
import { MasterView } from "@/features/master/MasterView";
import { ExportPage } from "@/features/master/ExportPage";
import { AnalysisPage } from "@/features/analysis/AnalysisPage";
import { IsolateDetail } from "@/features/isolate/IsolateDetail";
import { Dashboard } from "@/features/dashboard/Dashboard";
import { FlagQueue } from "@/features/qa/FlagQueue";
import { SyncQueue } from "@/features/queue/SyncQueue";
import { NotificationSettings } from "@/features/settings/NotificationSettings";
import { InboundQueue } from "@/features/inbound/InboundQueue";
import { PublicationsAdmin } from "@/features/admin/PublicationsAdmin";
import { BreakpointsAdmin } from "@/features/admin/BreakpointsAdmin";
import { UserAdmin } from "@/features/admin/UserAdmin";
import { ReferenceAdmin } from "@/features/admin/ReferenceAdmin";
import { AuditLogView } from "@/features/admin/AuditLogView";
import { SystemHealthView } from "@/features/admin/SystemHealthView";
import { HomePage } from "@/pages/HomePage";
import { DocumentationPage } from "@/pages/DocumentationPage";

export const router = createBrowserRouter([
  { path: "/", element: <HomePage /> },
  { path: "/home", element: <HomePage /> },
  { path: "/docs", element: <DocumentationPage /> },
  { path: "/documentation", element: <DocumentationPage /> },
  { path: "/login", element: <LoginForm /> },
  { path: "/signup", element: <SignupPage /> },
  {
    element: (
      <RequireAuth>
        <AppShell />
      </RequireAuth>
    ),
    children: [
      { path: "/workbench", element: <VolunteerHome /> },
      { path: "/entry", element: <EntryForm /> },
      { path: "/isolates", element: <MasterView /> },
      { path: "isolates/:id", element: <IsolateDetail /> },
      { path: "dashboard", element: <Dashboard /> },
      { path: "queue", element: <SyncQueue /> },
      { path: "settings/notifications", element: <NotificationSettings /> },
      { path: "laboratory", element: <InboundQueue /> },
      {
        path: "publish",
        element: (
          <RequireAuth roles={["data_manager", "microbiologist", "administrator"]}>
            <PublicationsAdmin />
          </RequireAuth>
        ),
      },
      {
        path: "breakpoints",
        element: (
          <RequireAuth roles={["microbiologist", "administrator"]}>
            <BreakpointsAdmin />
          </RequireAuth>
        ),
      },
      {
        path: "qa-flags",
        element: (
          <RequireAuth roles={["team_lead", "data_manager", "microbiologist", "administrator"]}>
            <FlagQueue />
          </RequireAuth>
        ),
      },
      {
        path: "antibiogram",
        element: (
          <RequireAuth roles={["data_manager", "microbiologist", "administrator"]}>
            <AnalysisPage />
          </RequireAuth>
        ),
      },
      {
        path: "export",
        element: (
          <RequireAuth roles={["data_manager", "microbiologist", "administrator"]}>
            <ExportPage />
          </RequireAuth>
        ),
      },
      {
        path: "reference",
        element: (
          <RequireAuth roles={["microbiologist", "administrator"]}>
            <ReferenceAdmin />
          </RequireAuth>
        ),
      },
      {
        path: "users",
        element: (
          <RequireAuth roles={["administrator", "data_manager"]}>
            <UserAdmin />
          </RequireAuth>
        ),
      },
      {
        path: "audit",
        element: (
          <RequireAuth roles={["administrator", "data_manager"]}>
            <AuditLogView />
          </RequireAuth>
        ),
      },
      {
        path: "system",
        element: (
          <RequireAuth roles={["administrator"]}>
            <SystemHealthView />
          </RequireAuth>
        ),
      },
      { path: "*", element: <Navigate to="/" replace /> },
    ],
  },
]);
